import type { AnyAction } from 'redux';
import type { GenericObject, Terms } from '@makemydeal/dr-platform-types';
import { BALLOON, FINANCE, LEASE } from '@makemydeal/dr-platform-types';
import { MULTI_SCENARIO_GRID_CONSISTENCY_TOGGLE } from '../constants/meta';
import { CALC_ENDPOINTS } from '../constants/state';
import { getDefaultTermId } from '../reducers/offerNodeUtils';
import { getTermsByIdPayment } from '../selectors/offerSelectors';
import type { OfferTermOffer } from '../types/offerTypes';
import type { TProgramInfoAll } from '../types/paymentTypes';
import { isUserPricedProgram, isValidProgram } from './lenderProgramUtils';
import { getLoadPayloadFeatureToggles } from './loadPayloadFeatureToggles';

export const EXPLICIT_PROGRAM_LOCK_META = 'explicitProgramLock';

const isDealProgram = (program: unknown): boolean => isValidProgram(program) && !isUserPricedProgram(program);

const getUpqResponseLock = (action: AnyAction): GenericObject | undefined => {
    if (action.meta?.requestCalculationEndpoint !== CALC_ENDPOINTS.userProgramQuotes) return undefined;
    const originalMeta: GenericObject | undefined = action.meta?.originalAction?.meta;
    // A grid-priced term is calculator-sourced: stamping the lock on it made its column read program-backed (DE774433).
    if (originalMeta?.paymentGrid?.gridAction || originalMeta?.gridCellSelectionOfferType) return undefined;
    const lock: GenericObject | undefined = originalMeta?.[EXPLICIT_PROGRAM_LOCK_META];
    return isDealProgram(lock) ? lock : undefined;
};

const getResponseTermIds = (action: AnyAction): string[] => Object.keys(action.payload?.terms?.byId ?? {});

export const keepLockedProgramOnUpqTerms = (terms: Terms, action: AnyAction): Terms => {
    const lock = getUpqResponseLock(action);
    if (!lock) return terms;

    const { creditTier, creditTierDesc, lenderLegalName, lenderCode, ...lenderProgram } = lock;
    const byId: GenericObject = { ...terms.byId };
    let kept = false;

    getResponseTermIds(action).forEach((termId) => {
        const term = byId[termId];
        if (!term || isDealProgram(term.lenderProgram)) return;
        byId[termId] = {
            ...term,
            lenderProgram,
            ...(lenderLegalName != null && { lenderLegalName }),
            ...(lenderCode != null && { lenderCode }),
            ...(term.creditTier == null && creditTier != null && { creditTier }),
            ...(term.creditTierDescription == null && creditTierDesc != null && { creditTierDescription: creditTierDesc })
        };
        kept = true;
    });

    return kept ? ({ ...terms, byId } as Terms) : terms;
};

export type UpqLockKeptTerms = Record<string, Record<number, true>>;

export const markUpqLockKeptTerms = (marks: UpqLockKeptTerms | undefined, action: AnyAction): UpqLockKeptTerms | undefined => {
    const lock = getUpqResponseLock(action);
    const responseTerms: GenericObject = action.payload?.terms?.byId ?? {};
    let next = marks;

    getResponseTermIds(action).forEach((termId) => {
        const offerType = termId.split('_')[0];
        const months = responseTerms[termId]?.months;
        if (months == null) return;
        const byMonths = next?.[offerType] ?? {};
        const pricedByProgram = isDealProgram(responseTerms[termId]?.lenderProgram);

        if (lock && !pricedByProgram) {
            if (byMonths[months]) return;
            next = { ...next, [offerType]: { ...byMonths, [months]: true } };
            return;
        }
        if (!pricedByProgram || !byMonths[months]) return;
        const { [months]: _cleared, ...rest } = byMonths;
        next = { ...next, [offerType]: rest };
    });

    return next;
};

export const markLoadedUpqLockKeptTerms = (payload: GenericObject | undefined): UpqLockKeptTerms | undefined => {
    if (!getLoadPayloadFeatureToggles(payload)?.[MULTI_SCENARIO_GRID_CONSISTENCY_TOGGLE]) return undefined;
    if (payload?.calculationDetails?.payment?.calculationEngine?.endpoint !== CALC_ENDPOINTS.userProgramQuotes) return undefined;

    const offer: GenericObject | undefined = payload?.offer;
    const offerType = offer?.type;
    if (offerType !== FINANCE && offerType !== LEASE && offerType !== BALLOON) return undefined;

    const term = getTermsByIdPayment(offer as OfferTermOffer, getDefaultTermId(offer as OfferTermOffer, offerType));
    if (term?.months == null) return undefined;
    const loadedProgram = term.lenderProgram?.programName ? term.lenderProgram : offer?.payment?.lenderProgram;
    if (!isDealProgram(loadedProgram)) return undefined;

    return { [offerType]: { [term.months]: true } };
};

export const keepLockedProgramInUpqCache = (programInfo: TProgramInfoAll, action: AnyAction): TProgramInfoAll => {
    const lock = getUpqResponseLock(action);
    if (!lock) return programInfo;

    const responseTerms: GenericObject = action.payload?.terms?.byId ?? {};
    const { lenderCode: _lenderCode, ...program } = lock;
    const kept: GenericObject = { ...programInfo };
    let changed = false;

    getResponseTermIds(action).forEach((termId) => {
        const offerType = termId.split('_')[0];
        const months = responseTerms[termId]?.months;
        const byMonths = (kept[offerType] as GenericObject | undefined) ?? {};
        if (months == null || isDealProgram(byMonths[months])) return;
        kept[offerType] = { ...byMonths, [months]: program };
        changed = true;
    });

    return changed ? (kept as TProgramInfoAll) : programInfo;
};
